import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'wsm-common-data';
import FacetGroup from '../FacetGroup';
import FacetTitle from '../FacetTitle';
import {
	FACET_OVERRIDE_TYPE_SELECT,
	priceFacetIds
} from '../../global-constants';
import { facetGroupHasSelections } from '../../utilities/clear-filters';

const NestedFacetPanel = ({
	facetGroup,
	allFiltersLabel,
	onBack,
	backLinkRef,
	panelHeadingRef,
	onResetFacet,
	onCloseModal
}) => {
	const {
		facet,
		selectedValues = [],
		detailsLinkData,
		openDetailsModal,
		isMultiFacetGroup,
		multiFacets,
		groupId: multiGroupId,
		groupTitleLabel
	} = facetGroup;

	const labels = useLabels();

	// Derive id and title the same way MultiFacetGroup does: multi-group
	// items carry groupId/groupTitleLabel directly; single-facet items
	// use the real facet object.
	const groupId = multiGroupId || facet?.id;
	const groupTitle = groupTitleLabel
		? labels.get(groupTitleLabel)
		: facet?.label;

	// In nested mode a facet panel's content — including any WIAPI
	// placeholder — isn't in the DOM until the user navigates into that
	// panel, so the page-load WIAPI insertion misses it. When a panel that
	// carries a placeholder mounts, dispatch `wiapiPlaceholderAvailable` so
	// ddc-js-api re-scans the DOM and inserts into the now-present
	// placeholder. Mirrors ws-inv-filters' FilterModalButton, which fires
	// the same event when the mobile filters modal opens. Only price facets
	// carry a placeholder today (see `priceFacetIds` in FacetGroup). Note:
	// this re-invokes integrations that used `API.insert()` with
	// `subscribe: true` (the default); `insertOnce` integrations are not
	// re-invoked. The effect runs post-commit, so the placeholder div is
	// guaranteed present when the event fires.
	useEffect(() => {
		if (priceFacetIds.includes(groupId)) {
			window.dispatchEvent(
				new CustomEvent('wiapiPlaceholderAvailable', {
					detail: { location: 'search-facet-pricing' }
				})
			);
		}
	}, [groupId]);

	const hasContentOrLabel =
		detailsLinkData &&
		(detailsLinkData.mainDetail ||
			detailsLinkData.subContent?.length !== 0);

	const showReset =
		typeof onResetFacet === 'function' &&
		facetGroupHasSelections(facetGroup);
	const showCloseModal = typeof onCloseModal === 'function';
	const resetLabel = labels.get('RESET');
	const closeLabel = labels.get('CLOSE_FILTERS');

	const renderFacetContent = () => {
		if (isMultiFacetGroup && multiFacets) {
			return (
				<>
					{multiFacets.map(({ facet: subFacet, facetSelections }) => (
						<FacetGroup
							facet={subFacet}
							selectedValues={
								facetSelections ? facetSelections.values : []
							}
							renderPanel={false}
							includeExtras={false}
							overrideType={FACET_OVERRIDE_TYPE_SELECT}
							key={`nested-multi-${subFacet.id}`}
						/>
					))}
					{hasContentOrLabel && (
						<button
							type="button"
							className="text-link mt-3 text-decoration-underline text-primary text-left"
							onClick={() =>
								openDetailsModal(
									groupId,
									groupTitle,
									detailsLinkData,
									true
								)
							}
						>
							<span className="ddc-font-size-xsmall">
								{labels.get('ABOUT')} {groupTitle}
							</span>
						</button>
					)}
				</>
			);
		}

		return (
			<FacetGroup
				facet={facet}
				selectedValues={selectedValues}
				renderPanel={false}
				detailsLinkData={detailsLinkData}
				openDetailsModal={openDetailsModal}
			/>
		);
	};

	return (
		<div
			className="nested-facet-detail pt-1"
			role="region"
			aria-label={groupTitle}
		>
			<div className="nested-facet-detail-header d-flex align-items-center justify-content-between pt-2 pb-3">
				<button
					type="button"
					ref={backLinkRef}
					className="btn btn-unstyled d-flex align-items-center pl-0 pr-3 border-0 text-left"
					onClick={onBack}
					aria-label={`${labels.get('BACK_TO')} ${allFiltersLabel}`}
				>
					<i
						className="ddc-icon ddc-icon-skinny-arrow-left mr-2"
						aria-hidden="true"
					/>
					<span>{allFiltersLabel}</span>
				</button>
				<div className="d-flex align-items-center">
					{showReset && (
						<button
							type="button"
							className="btn btn-unstyled text-link-muted pr-0 pl-3 py-3 m-0 d-none d-lg-inline-block"
							onClick={onResetFacet}
							aria-label={`${resetLabel} ${groupTitle}`}
						>
							{resetLabel}
						</button>
					)}
					{showCloseModal && (
						<button
							type="button"
							className="btn btn-unstyled d-inline-flex d-lg-none align-items-center justify-content-center p-3"
							onClick={onCloseModal}
							aria-label={closeLabel}
						>
							<i
								className="ddc-icon ddc-icon-remove2 ddc-icon-size-large"
								aria-hidden="true"
							/>
						</button>
					)}
				</div>
			</div>
			<h3
				ref={panelHeadingRef}
				tabIndex="-1"
				className="nested-facet-detail-title"
			>
				<FacetTitle facetTitle={groupTitle} groupId={groupId} />
			</h3>
			<div className="nested-facet-detail-content pt-2">
				{renderFacetContent()}
			</div>
		</div>
	);
};

NestedFacetPanel.propTypes = {
	facetGroup: PropTypes.shape({
		facet: PropTypes.shape({
			id: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired
		}),
		groupId: PropTypes.string,
		groupTitleLabel: PropTypes.string,
		selectedValues: PropTypes.arrayOf(PropTypes.shape({})),
		detailsLinkData: PropTypes.shape({
			mainDetail: PropTypes.string,
			subContent: PropTypes.arrayOf(PropTypes.shape({}))
		}),
		openDetailsModal: PropTypes.func,
		isMultiFacetGroup: PropTypes.bool,
		multiFacets: PropTypes.arrayOf(PropTypes.shape({}))
	}).isRequired,
	allFiltersLabel: PropTypes.string.isRequired,
	onBack: PropTypes.func.isRequired,
	backLinkRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
	panelHeadingRef: PropTypes.shape({
		current: PropTypes.instanceOf(Element)
	}),
	onResetFacet: PropTypes.func,
	onCloseModal: PropTypes.func
};

export default NestedFacetPanel;
